import { THEME_URL } from './constants.js';
import { toggleHeader } from './header.js';

// Navigation and menu functionality
export function initializeNavigation() {
  // Cache DOM elements
  const elements = {
    header: document.querySelector('.site-header'),
    customLogoLink: document.querySelector('.custom-logo-link img'),
    searchIcon: document.querySelector('.search-button img'),
    mobileIcon: document.querySelector('.mobile-icon'),
    menuWrapper: document.querySelector('.menu-wrapper'),
    overlay: document.querySelector('.menu-overlay'),
    subMenus: document.querySelectorAll('.menu-sub-wrapper'),
  };

  // Helper functions
  const helpers = {
    isDesktop: () => window.innerWidth > 990,
    isMobile: () => window.innerWidth < 990,
    isScrolled: () => window.scrollY > 50,
    hasTransparentHeader: () => document.body.classList.contains('has-transparent-header'),

    // Update header appearance based on scroll and transparent header state
    updateHeaderAppearance: () => {
      const { header, customLogoLink, searchIcon } = elements;
      const isScrolled = helpers.isScrolled();
      const isDesktop = helpers.isDesktop();
      const hasTransparentHeader = helpers.hasTransparentHeader();

      if (!header || !customLogoLink || !searchIcon) return;

      if (isDesktop) {
        if (isScrolled) {
          // Scrolled on desktop - show dark theme
          header.classList.add('is-white-bg');
          customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
          searchIcon.src = `${THEME_URL}/images/icon-search-dark.svg`;
        } else {
          // Not scrolled on desktop - handle transparent header
          if (hasTransparentHeader) {
            header.classList.remove('is-white-bg');
            customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo.svg`;
            searchIcon.src = `${THEME_URL}/images/icon-search.svg`;
          } else {
            // No transparent header - always dark theme
            header.classList.add('is-white-bg');
            customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
            searchIcon.src = `${THEME_URL}/images/icon-search-dark.svg`;
          }
        }
      } else if (helpers.isMobile()) {
        if (isScrolled) {
          // Scrolled on mobile - show dark theme
          header.classList.add('is-white-bg');
          customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
          searchIcon.src = `${THEME_URL}/images/icon-search-dark.svg`;
        } else {
          // Not scrolled on mobile
          header.classList.remove('is-white-bg');
          if (hasTransparentHeader) {
            searchIcon.src = `${THEME_URL}/images/icon-search.svg`;
            customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo.svg`;
          }
        }
      }
    },

    // Close all submenus
    closeAllSubmenus: () => {
      elements.subMenus.forEach((subMenu) => subMenu.classList.remove('active'));
    },

    // Reset overlay and menu state
    resetMenuState: () => {
      if (elements.overlay) {
        elements.overlay.classList.add('hide-overlay');
      }
      document.body.classList.remove('has-open-menu');
    },

    // Update icons based on current state
    updateIcons: (isDark = false) => {
      const { customLogoLink, searchIcon } = elements;
      if (!customLogoLink || !searchIcon) return;

      if (isDark) {
        customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
        searchIcon.src = `${THEME_URL}/images/icon-search-dark.svg`;
      } else {
        const hasTransparentHeader = helpers.hasTransparentHeader();
        if (hasTransparentHeader) {
          customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo.svg`;
          searchIcon.src = `${THEME_URL}/images/icon-search.svg`;
        } else {
          customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
          searchIcon.src = `${THEME_URL}/images/icon-search-dark.svg`;
        }
      }
    },
  };

  // Initialize header state
  const initializeHeader = () => {
    const { header, customLogoLink, searchIcon, mobileIcon } = elements;
    const hasTransparentHeader = helpers.hasTransparentHeader();

    // If no transparent header, start with white background and dark icons
    if (!hasTransparentHeader) {
      header?.classList.add('is-white-bg');
      helpers.updateIcons(true);
    }

    // Check initial scroll position for transparent header pages
    if (hasTransparentHeader && helpers.isScrolled() && helpers.isDesktop()) {
      toggleHeader();
    }

    // Set mobile icon state
    if (mobileIcon) {
      if (hasTransparentHeader) {
        mobileIcon.classList.add('is-white-menu');
      } else {
        mobileIcon.classList.remove('is-white-menu');
      }
    }

    // Ensure initial icons match current state (e.g., white icons on transparent header at top)
    helpers.updateHeaderAppearance();
  };

  // Handle anchor link clicks
  const handleAnchorLinks = () => {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        const targetId = this.getAttribute('href');
        const isSamePage =
          this.pathname === window.location.pathname && this.hostname === window.location.hostname;

        if (isSamePage) {
          e.preventDefault();
          import('./anchors.js').then(({ handleAnchor }) => {
            handleAnchor(targetId);
          });
          history.pushState(null, null, targetId);
        }
      });
    });
  };

  // Handle mobile menu toggle
  const handleMobileMenu = () => {
    if (!elements.mobileIcon) return;

    elements.mobileIcon.addEventListener('click', () => {
      const { menuWrapper, header } = elements;
      const hasTransparentHeader = helpers.hasTransparentHeader();
      const isScrolled = helpers.isScrolled();

      // Toggle mobile menu state
      document.body.classList.toggle('overflow-hidden');
      menuWrapper?.classList.toggle('mobile-opened');
      header?.classList.toggle('mobile-opened');
      elements.mobileIcon?.classList.toggle('is-white-menu');

      // Handle transparent header behavior
      if (hasTransparentHeader) {
        helpers.updateIcons(true);
      } else {
        elements.mobileIcon?.classList.remove('is-white-menu');
      }

      // Handle logo state when not scrolled
      if (!isScrolled && elements.customLogoLink) {
        const isMenuOpened = menuWrapper?.classList.contains('mobile-opened');
        if (isMenuOpened) {
          elements.customLogoLink.src = `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
        } else {
          elements.customLogoLink.src = hasTransparentHeader
            ? `${THEME_URL}/images/logo.svg`
            : `${THEME_URL}/images/logo-empower-analog/logo-dark.svg`;
        }
      }
    });
  };

  // Handle submenu interactions
  const handleSubmenus = () => {
    document.querySelectorAll('.header-menu-parent-item').forEach((item) => {
      // Desktop behavior - use mouseenter
      item.addEventListener('mouseenter', function () {
        if (helpers.isMobile()) return; // Skip on mobile

        if (document.body.classList.contains('has-open-menu')) return;

        document.body.classList.add('has-open-menu');
        const overlay = elements.overlay;
        const subMenus = elements.subMenus;

        overlay?.classList.toggle('hide-overlay');
        // Show all submenus on desktop
        subMenus.forEach((subMenu) => {
          subMenu.classList.add('active');
        });

        // Handle transparent header
        if (helpers.hasTransparentHeader()) {
          toggleHeader();
        }

        if (helpers.hasTransparentHeader() && helpers.isScrolled()) {
          elements.header?.classList.toggle('is-white-bg');
          helpers.updateIcons(true);
        }
      });

      // Mobile behavior - use click/touch with submenu logic
      item.addEventListener('click', function (e) {
        if (helpers.isDesktop()) return; // Skip on desktop

        const nearSubMenu = item.nextElementSibling;
        const hasSubmenu = nearSubMenu?.classList.contains('menu-sub-wrapper');
        const hasSubmenuItems = hasSubmenu && nearSubMenu.children.length > 0;

        if (hasSubmenu && !hasSubmenuItems) {
          return;
        }

        if (hasSubmenuItems) {
          const isSubmenuOpen = nearSubMenu.classList.contains('active');

          if (!isSubmenuOpen) {
            // First touch - prevent navigation and show submenu
            e.preventDefault();

            document.body.classList.add('has-open-menu');

            // Close all other submenus first
            elements.subMenus.forEach((subMenu) => {
              if (subMenu !== nearSubMenu) {
                subMenu.classList.remove('active');
              }
            });

            // Show this submenu
            nearSubMenu.classList.add('active');

            // On mobile, when menu is opened, keep everything in white/light version
            // Don't add white background or change to dark icons
            if (helpers.hasTransparentHeader()) {
              // toggleHeader();
              // Keep light icons - don't call helpers.updateIcons(true)
            }
          }
          // Second touch - allow navigation (don't prevent default)
          // The link will navigate naturally
        }
        // If no submenu, allow normal navigation
      });
    });
  };

  // Handle overlay interactions
  const handleOverlay = () => {
    if (!elements.overlay) return;

    elements.overlay.addEventListener('mouseenter', function () {
      if (helpers.isMobile()) return; // Skip overlay behavior on mobile

      helpers.closeAllSubmenus();
      helpers.resetMenuState();

      // Reset header and icons
      if (helpers.hasTransparentHeader()) {
        helpers.updateIcons(false);
        elements.header?.classList.remove('is-white-bg');
      } else {
        helpers.updateIcons(true);
      }

      // Handle scrolled state
      if (helpers.isScrolled()) {
        elements.header?.classList.add('is-white-bg');
        helpers.updateIcons(true);
      }
    });

    // Close submenus when touching/clicking overlay on mobile
    elements.overlay.addEventListener('click', function () {
      if (helpers.isDesktop()) return; // Skip on desktop

      helpers.closeAllSubmenus();
      helpers.resetMenuState();
    });
  };

  // Handle scroll behavior
  const handleScroll = () => {
    window.addEventListener('scroll', () => {
      helpers.resetMenuState();
      helpers.closeAllSubmenus();
      helpers.updateHeaderAppearance();
    });
  };

  // Close submenus when touching outside on mobile
  const handleOutsideTouch = () => {
    document.addEventListener('touchstart', (e) => {
      if (helpers.isDesktop()) return;

      // Check if touch is outside menu items
      const isMenuTouch =
        e.target.closest('.header-menu-parent-item') || e.target.closest('.menu-sub-wrapper');

      if (!isMenuTouch) {
        helpers.closeAllSubmenus();
        helpers.resetMenuState();
      }
    });
  };

  // Initialize everything
  const init = () => {
    initializeHeader();
    handleAnchorLinks();
    handleMobileMenu();
    handleSubmenus();
    handleOverlay();
    handleScroll();
    handleOutsideTouch();
  };

  // Start initialization
  init();
}
